import React, { useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import Input from '@/components/General/inputs/Input';
import Checkbox from '@/components/General/inputs/Checkbox';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import { Query } from '@/utils/react-query/Queries';
import { SERVICE_FLOW_ITEM_TYPES, SERVICE_FLOW_TASK_DUE_TYPE } from '@/config/serviceFlows';
import { validateFlowItem } from '@/utils/serviceFlow';
import ServiceFlowWorshipSummary from './ServiceFlowWorshipSummary';
import ServiceFlowNotesField from './ServiceFlowNotesField';
import { serviceFlowStyles } from './serviceFlowStyles';

/**
 * Focused single-item editor (spec §3.4/§3.5) — title, duration, notes, and
 * type-specific fields. Never a multi-item form. Item timing is derived
 * sequentially from durations; `showTime` opts a row into showing that
 * derived time on the timeline. Controlled: parent owns the draft item and
 * commits. Save/cancel live in the host — SheetModal header confirm or the
 * workspace side-panel header — not in this form.
 *
 * @param {Object} props
 * @param {Object} props.item - normalized draft item
 * @param {Function} props.onChange - (patch) update the draft item
 * @param {boolean} [props.saving]
 * @param {boolean} [props.allowTypeChange] - template drafts only
 * @param {Function} [props.onTypeChange] - (type) parent confirms before clearing task fields
 * @param {string} [props.fromScreen]
 * @param {boolean} [props.canCreateTasks]
 */
const ServiceFlowItemEditor = ({
    item,
    onChange,
    saving = false,
    allowTypeChange = false,
    onTypeChange = null,
    fromScreen = null,
    canCreateTasks = false,
    taskIdentityLocked = false,
    locationID = null,
}) => {
    const [duration, setDuration] = useState(item.durationMinutes == null ? '' : String(item.durationMinutes));
    const errors = useMemo(() => validateFlowItem(item), [item]);

    // TASK pickers — only EVENT_SERVICE_FLOW tasks are selectable (spec §4.3).
    const serviceFlowTasksQuery = Query.tasks.byDueType({
        dueType: SERVICE_FLOW_TASK_DUE_TYPE,
        options: { enabled: item.type === 'TASK' },
    });
    const { data: teams } = Query.taskTeams.all({ options: { enabled: item.type === 'TASK' } });
    // Teams that include the selected task — others are excluded from the sheet.
    const ineligibleTeamIDs = useMemo(
        () => (teams || [])
            .filter(t => !(t.Tasks || []).some(task => task.id === item.taskID) || (locationID && t.locationID && t.locationID !== locationID))
            .map(t => t.id),
        [teams, item.taskID, locationID]
    );

    return (
        <View style={serviceFlowStyles.editorContainer} testID="service-flow-item-editor">
            {allowTypeChange && (
                <View>
                    <Text style={serviceFlowStyles.editorFieldLabel}>Type</Text>
                    <ToggleInput
                        items={Object.values(SERVICE_FLOW_ITEM_TYPES)
                        .filter(t => !t.hiddenInPicker || t.type === item.type)
                        .map(t => ({ label: t.label, value: t.type }))}
                        selectedValues={[item.type]}
                        onItemChanged={(value) => { if (value !== item.type) onTypeChange?.(value); }}
                        disabled={saving}
                    />
                </View>
            )}

            <View>
                <Input
                    title="Title"
                    isNotOnSurface
                    required
                    value={item.title}
                    disabled={saving}
                    onUpdate={(v) => onChange({ title: v })}
                    testID="service-flow-item-title"
                />
                {!!errors.title && <Text style={serviceFlowStyles.editorError}>{errors.title}</Text>}
            </View>

            <View>
                <Input
                    title="Duration (minutes)"
                    isNotOnSurface
                    type="numeric"
                    value={duration}
                    disabled={saving}
                    placeholder="Not set"
                    onUpdate={(v) => {
                        setDuration(v);
                        onChange({ durationMinutes: v.trim() === '' ? null : Number(v) });
                    }}
                    helpText="Leave empty for no set duration."
                    testID="service-flow-item-duration"
                />
                {!!errors.durationMinutes && <Text style={serviceFlowStyles.editorError}>{errors.durationMinutes}</Text>}
            </View>

            <Checkbox
                checked={!!item.showTime}
                onChange={(v) => onChange({ showTime: v })}
                disabled={saving}
                label="Show time based on duration"
                testID="service-flow-item-show-time"
            />

            {item.type === 'TASK' && (
                <>
                    <View style={serviceFlowStyles.editorSection}>
                        <SheetSelector
                            type="tasks"
                            queryResult={serviceFlowTasksQuery}
                            title="Service Flow Task"
                            placeholder="Select a task"
                            disabled={saving || taskIdentityLocked}
                            titleOnly
                            isNotOnSurface
                            fromScreen={fromScreen}
                            addScreenName={canCreateTasks ? '/tasks/create' : null}
                            addScreenParams={{ dueType: SERVICE_FLOW_TASK_DUE_TYPE }}
                            value={item.Task || serviceFlowTasksQuery.data?.find(task => task.id === item.taskID) || (item.taskID ? { id: item.taskID, title: 'Selected task' } : null)}
                            onChange={(selected) => onChange({
                                taskID: selected?.id || null,
                                Task: selected || null,
                                volunteerTeamID: null, // team must belong to the new task
                                VolunteerTeam: null,
                            })}
                        />
                        {!!errors.taskID && <Text style={serviceFlowStyles.editorError}>{errors.taskID}</Text>}
                        <Text style={serviceFlowStyles.helperText}>Assignments and responses are managed on each date.</Text>
                    </View>

                    {!!item.taskID && (
                        <View style={serviceFlowStyles.editorSection}>
                            <SheetSelector
                                type="taskTeams"
                                title="Volunteer Team"
                                placeholder="Optional team"
                                disabled={saving || taskIdentityLocked}
                                titleOnly
                                isNotOnSurface
                                fromScreen={fromScreen}
                                value={item.VolunteerTeam || teams?.find(team => team.id === item.volunteerTeamID) || (item.volunteerTeamID ? { id: item.volunteerTeamID, name: 'Selected team' } : null)}
                                onChange={(selected) => onChange({ volunteerTeamID: selected?.id || null, VolunteerTeam: selected || null })}
                                excludeIds={ineligibleTeamIDs}
                            />
                            <Text style={serviceFlowStyles.helperText}>
                                {taskIdentityLocked
                                    ? 'Task history protects this task and team. You can still change its title, timing, and notes.'
                                    : 'Choose a team that includes this task, or leave empty for admin-managed assignments.'}
                            </Text>
                        </View>
                    )}
                </>
            )}

            {item.type === 'WORSHIP' && <ServiceFlowWorshipSummary />}

            <View>
                <ServiceFlowNotesField
                    value={item.description || ''}
                    disabled={saving}
                    onUpdate={(v) => onChange({ description: v })}
                    testID="service-flow-item-notes"
                />
                {!!errors.description && <Text style={serviceFlowStyles.editorError}>{errors.description}</Text>}
            </View>
        </View>
    );
};

export default ServiceFlowItemEditor;
